import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ContentCopy, Delete, Favorite, FavoriteBorder, Visibility } from '@mui/icons-material';
import Build from '../../core/Build';
import { canonicalBuildPath, localGenPath } from '../../utils/buildRoutes';
import { formatCount } from '../../utils/formatCount';
import BuildCharacter from './BuildCharacter';
import BuildTooltip from './BuildTooltip';
import CriteriaTagStrip from '../generator/CriteriaTagStrip';
import Tooltip from '../utils/Tooltip';
import {
    buildElementGradient,
    buildElementGradientHover,
    buildElementGradientActive,
    buildElementGradientActiveHover,
} from '../utils/CardGradient';
import './CompactBuildCard.css';

const TXT_UNNAMED = { fr: 'Sans nom', en: 'Unnamed', de: 'Ohne Namen', es: 'Sin nombre', pt: 'Sem nome' };
const TXT_DUPLICATE = { fr: 'Dupliquer', en: 'Duplicate', de: 'Duplizieren', es: 'Duplicar', pt: 'Duplicar' };
const TXT_DELETE = { fr: 'Supprimer', en: 'Delete', de: 'Löschen', es: 'Eliminar', pt: 'Excluir' };
const TAG_ICON_MAX = 4;
const DATE_DISPLAY_OPTIONS = { day: '2-digit', month: 'short', year: 'numeric' };
const VARIANT_OWNED = 'owned';

function formatDate(iso) {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString(undefined, DATE_DISPLAY_OPTIONS);
    } catch {
        return '';
    }
}

function isPlainLeftClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Compact horizontal build teaser.
 * variant "community" (default): owner + likes/views.
 * variant "owned": modified date; bottom bar: duplicate/delete + likes + views.
 */
export function CompactBuildCard({
    entry,
    lang,
    onToggleLike,
    hideOwner = false,
    variant = 'community',
    isActive = false,
    onActivate,
    onDelete,
    onDuplicate,
}) {
    const owned = variant === VARIANT_OWNED;
    const raw = entry?.build;
    const lvl = raw?.lvl ?? 200;
    const hasName = !!entry?.name?.trim();
    const name = hasName ? entry.name.trim() : (TXT_UNNAMED[lang] || TXT_UNNAMED.en);
    const likes = formatCount(entry?.metrics?.likes, lang);
    const views = formatCount(entry?.metrics?.views, lang);
    const hasLiked = !!entry?.viewer?.hasLiked;
    const ownerName = entry?.owner?.display_name || entry?.owner?.public_slug || 'Unknown';
    const ownerSlug = entry?.owner?.public_slug;
    const publicCode = entry?.publicCode;
    const canLike = typeof onToggleLike === 'function';
    const tags = raw?.criteriaTags;
    const modifiedLabel = formatDate(entry?.updatedAt || entry?.createdAt);
    const txtDuplicate = TXT_DUPLICATE[lang] || TXT_DUPLICATE.en;
    const txtDelete = TXT_DELETE[lang] || TXT_DELETE.en;

    const href = (ownerSlug && publicCode)
        ? canonicalBuildPath(lang, ownerSlug, publicCode)
        : localGenPath(lang);

    const buildModel = useMemo(() => new Build(raw || {}), [raw]);
    const cls = buildModel.getCharacterClass?.();
    const classIcon = cls?.icon || '';
    const className = cls?.label?.[lang] || cls?.label?.en || cls?.name || '';

    const gradVars = useMemo(() => {
        // The colour, not the stat sheet: on a 48-card grid this is the
        // difference between deriving stats from 16 items per card and reading
        // one stored triple. The gradient helpers accept either.
        const color = buildModel.getElementTint();
        if (isActive) {
            return {
                '--compact-build-grad': buildElementGradientActive(color),
                '--compact-build-grad-hover': buildElementGradientActiveHover(color),
            };
        }
        return {
            '--compact-build-grad': buildElementGradient(color),
            '--compact-build-grad-hover': buildElementGradientHover(color),
        };
    }, [buildModel, isActive]);

    const onLinkClick = (e) => {
        if (!onActivate || !isPlainLeftClick(e)) return;
        e.preventDefault();
        onActivate();
    };

    const ownerBlock = ownerSlug ? (
        <Link
            to={`/${lang}/community/${encodeURIComponent(ownerSlug)}`}
            className="compact-build-owner"
        >
            {entry?.owner?.avatar_url ? (
                <img src={entry.owner.avatar_url} alt="" className="compact-build-owner-avatar" />
            ) : (
                <span className="compact-build-owner-avatar compact-build-owner-avatar--empty" aria-hidden />
            )}
            <span className="compact-build-owner-name">{ownerName}</span>
        </Link>
    ) : (
        <span className="compact-build-owner">
            <span className="compact-build-owner-avatar compact-build-owner-avatar--empty" aria-hidden />
            <span className="compact-build-owner-name">{ownerName}</span>
        </span>
    );

    const dateBlock = modifiedLabel ? (
        <span className="compact-build-date">{modifiedLabel}</span>
    ) : null;

    const likeMetric = canLike ? (
        <button
            type="button"
            className={`compact-build-like-btn${hasLiked ? ' active' : ''}`}
            onClick={() => onToggleLike(entry.id, hasLiked)}
        >
            {hasLiked ? <Favorite fontSize="inherit" aria-hidden /> : <FavoriteBorder fontSize="inherit" aria-hidden />}
            <span>{likes}</span>
        </button>
    ) : (
        <span className="compact-build-meta-metric">
            <Favorite fontSize="inherit" aria-hidden />
            <span>{likes}</span>
        </span>
    );

    const leadingMeta = owned
        ? dateBlock
        : (hideOwner ? null : ownerBlock);

    return (
        <BuildTooltip
            build={buildModel}
            lang={lang}
            genConfig={entry?.genConfig}
            // Lets the panel fetch the gear the listing deliberately omitted.
            entry={entry}
        >
            <div
                className={`compact-build-card${isActive ? ' compact-build-card--active' : ''}`}
                style={gradVars}
            >
                <Link
                    to={href}
                    className="compact-build-card-link"
                    aria-label={name}
                    onClick={onLinkClick}
                />
                <div className="compact-build-card-main">
                    <div className="compact-build-card-body">
                        {leadingMeta}
                        <h3 className={`compact-build-card-name${hasName ? '' : ' compact-build-card-name--unnamed'}`}>{name}</h3>
                        <div className="compact-build-card-meta">
                            <span className="compact-build-meta-lvl">
                                {classIcon ? (
                                    <img
                                        src={classIcon}
                                        alt=""
                                        className="compact-build-class-icon"
                                        title={className}
                                        draggable={false}
                                    />
                                ) : null}
                                <span>{lvl}</span>
                            </span>
                            {tags?.length > 0 ? (
                                <CriteriaTagStrip
                                    tags={tags}
                                    lang={lang}
                                    className="compact-build-tags"
                                    maxIcons={TAG_ICON_MAX}
                                    fadeOverflow
                                    showTooltip={false}
                                />
                            ) : null}
                        </div>
                    </div>
                    <div className="compact-build-card-preview" aria-hidden="true">
                        <BuildCharacter build={buildModel} enableSkinRender={false} />
                    </div>
                </div>
                <div className="compact-build-card-bottombar">
                    <div className="compact-build-card-metrics">
                        {owned && typeof onDuplicate === 'function' ? (
                            <Tooltip content={txtDuplicate}>
                                <button
                                    type="button"
                                    className="compact-build-action-btn"
                                    aria-label={txtDuplicate}
                                    onClick={() => onDuplicate(entry.id)}
                                >
                                    <ContentCopy fontSize="inherit" aria-hidden />
                                </button>
                            </Tooltip>
                        ) : null}
                        {owned && typeof onDelete === 'function' ? (
                            <Tooltip content={txtDelete}>
                                <button
                                    type="button"
                                    className="compact-build-action-btn compact-build-action-btn--danger"
                                    aria-label={txtDelete}
                                    onClick={() => onDelete(entry.id)}
                                >
                                    <Delete fontSize="inherit" aria-hidden />
                                </button>
                            </Tooltip>
                        ) : null}
                        {likeMetric}
                        <span className="compact-build-meta-metric">
                            <Visibility fontSize="inherit" aria-hidden />
                            <span>{views}</span>
                        </span>
                    </div>
                </div>
            </div>
        </BuildTooltip>
    );
}
